// The scenario's welcome, resolved against the seat a person actually took.
//
// One copy, because it is used in two places that must never disagree: the dialog shown on
// arrival, and the demo drawer inside the plan. The drawer had its own handling once and
// printed a raw `{{character}}` at a person, which is what this module exists to stop.

import type { DemoSeatIntro } from './useDemoSeatIntro.js';

/** What an author writes in a scenario's `welcome` to mean "whoever they are playing". */
export const CHARACTER_PLACEHOLDER = '{{character}}';

/**
 * The welcome's paragraphs with the placeholder filled in.
 *
 * Filled in with `standaloneIntroduction`, never `introduction` — the placeholder stands
 * alone in the dialog with no paragraph above it to say whose family this is, which
 * `standaloneIntroduction` is written to not need and `introduction` is written to rely
 * on. A scenario written before `standaloneIntroduction` existed falls back to
 * `introduction`, so it still reads, just less well, rather than dropping the paragraph.
 *
 * A paragraph that names the placeholder when neither is written is left out rather than
 * shown with the raw token or a gap where a name should be: the seat is unknown only when
 * a read failed, and a sentence about who you are is not worth printing half of.
 */
export function welcomeParagraphs(seat: DemoSeatIntro): string[] {
  const resolved =
    seat.standaloneIntroduction !== '' ? seat.standaloneIntroduction : seat.introduction;
  return seat.welcome
    .filter((paragraph) => !paragraph.includes(CHARACTER_PLACEHOLDER) || resolved !== '')
    .map((paragraph) => paragraph.replaceAll(CHARACTER_PLACEHOLDER, resolved));
}
